import { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from '../api/client.js';
import { useDate, useT } from '../i18n/index.js';
import { Badge, Banner, Button, Card, Empty, Spinner } from '../components/ui.js';
import { ZoneCalibrator } from './ZoneCalibrator.js';

/**
 * PANEL DE ADMINISTRACION.
 *
 * Concentra lo que decide la Fase 2: la cola de excepciones ordenada por
 * confianza, el muestreo aleatorio y los KPI con los que se ACEPTA la fase
 * ("SKU por hora de persona" y "tasa de error de muestreo"), mas el taller y las
 * tarifas.
 */
export function AdminPage() {
  const t = useT();
  const location = useLocation();

  const tabs = [
    { to: '/admin', label: t('admin.kpi.title') },
    { to: '/admin/excepciones', label: t('admin.exceptions') },
    { to: '/admin/muestreo', label: t('admin.sampling') },
    { to: '/admin/calibrador', label: t('admin.calibration') },
    { to: '/admin/tarifas', label: t('admin.pricing') },
    { to: '/admin/taller', label: t('admin.workshop') },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <h1 className="mb-4 text-2xl font-semibold">{t('admin.title')}</h1>

      <nav className="-mx-4 mb-5 flex gap-2 overflow-x-auto px-4 pb-1">
        {tabs.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            className={`toque shrink-0 rounded-full border px-3 py-1.5 text-sm ${
              location.pathname === tab.to
                ? 'border-marca bg-marca text-white'
                : 'border-borde bg-white'
            }`}
          >
            {tab.label}
          </Link>
        ))}
      </nav>

      <Routes>
        <Route index element={<KpiPanel />} />
        <Route path="excepciones" element={<ExceptionsPanel />} />
        <Route path="muestreo" element={<SamplingPanel />} />
        <Route path="calibrador" element={<ZoneCalibrator />} />
        <Route path="tarifas" element={<PricingPanel />} />
        <Route path="taller" element={<WorkshopPanel />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </div>
  );
}

function KpiPanel() {
  const t = useT();
  const [kpi, setKpi] = useState<Awaited<ReturnType<typeof api.adminKpi>> | null>(null);

  useEffect(() => {
    void api.adminKpi().then(setKpi);
  }, []);

  if (!kpi) return <Spinner />;

  const coverage = Math.round(kpi.coverageRatio * 100);

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Metric
        label={t('admin.kpi.coverage')}
        value={`${coverage}%`}
        detail={`${kpi.skusCalibrated} / ${kpi.skusTotal} SKU`}
        // La Fase 2 se acepta con 80 % de cobertura: el numero se ve, no se cree.
        tone={coverage >= 80 ? 'ok' : 'warning'}
      />
      <Metric label={t('admin.kpi.skusPerHour')} value={kpi.skusPerPersonHour7d.toFixed(1)} detail="7 días" />
      <Metric
        label={t('admin.kpi.samplingError')}
        value={`${(kpi.samplingErrorRate * 100).toFixed(1)}%`}
        detail={`${kpi.samplingReviewed} ${t('admin.kpi.reviewed').toLowerCase()}`}
        tone={kpi.samplingErrorRate > 0.1 ? 'error' : 'ok'}
      />
      <Metric
        label={t('admin.kpi.openExceptions')}
        value={String(kpi.openExceptions)}
        tone={kpi.openExceptions > 0 ? 'warning' : 'ok'}
      />
    </div>
  );
}

function Metric({
  label,
  value,
  detail,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: 'neutral' | 'ok' | 'warning' | 'error';
}) {
  const colors: Record<string, string> = {
    neutral: 'text-tinta',
    ok: 'text-ok',
    warning: 'text-aviso',
    error: 'text-error',
  };
  return (
    <Card>
      <p className="text-xs uppercase tracking-wide text-tinta-suave">{label}</p>
      <p className={`mt-1 text-3xl font-semibold tabular-nums ${colors[tone]}`}>{value}</p>
      {detail ? <p className="text-xs text-tinta-suave">{detail}</p> : null}
    </Card>
  );
}

function ExceptionsPanel() {
  const t = useT();
  const formatDate = useDate();
  const [items, setItems] = useState<Awaited<ReturnType<typeof api.adminExceptions>>['items']>([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.adminExceptions({ status: 'open' }).then((response) => {
      setItems(response.items);
      setLoading(false);
    });

  useEffect(() => {
    void load();
  }, []);

  if (loading) return <Spinner />;
  if (items.length === 0) return <Empty title="Sin excepciones abiertas" />;

  return (
    <Card>
      <p className="mb-3 text-sm text-tinta-suave">
        Ordenadas por confianza ASCENDENTE: arriba lo que más probablemente está mal.
      </p>
      <ul className="divide-y divide-borde">
        {items.map((item) => (
          <li key={item.id} className="flex flex-wrap items-center gap-3 py-3">
            <span className="min-w-0 flex-1">
              <span className="block font-medium">
                {item.productCode} · {item.zoneId}
              </span>
              <span className="block text-xs text-tinta-suave">
                {item.reason} · {formatDate(item.createdAt)}
                {item.note ? ` · ${item.note}` : ''}
              </span>
            </span>
            <Badge tone={item.confidence < 0.5 ? 'error' : 'warning'}>
              {Math.round(item.confidence * 100)}%
            </Badge>
            <Button
              variant="secondary"
              onClick={async () => {
                await api.resolveException(item.id);
                await load();
              }}
            >
              {t('app.confirm')}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SamplingPanel() {
  const t = useT();
  const [items, setItems] = useState<Awaited<ReturnType<typeof api.adminSampling>>['items']>([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.adminSampling().then((response) => {
      setItems(response.items);
      setLoading(false);
    });

  useEffect(() => {
    void load();
  }, []);

  if (loading) return <Spinner />;

  return (
    <Card className="space-y-3">
      <Banner tone="info" title="Muestreo aleatorio obligatorio">
        Se revisan zonas HEREDADAS con confianza alta. Las de confianza baja ya están en la cola de excepciones; el
        riesgo real está en las que el sistema cree buenas.
      </Banner>

      <Button
        variant="secondary"
        onClick={async () => {
          await api.scheduleSampling(0.05);
          await load();
        }}
      >
        Programar muestreo (5 %)
      </Button>

      {items.length === 0 ? (
        <Empty title="Nada pendiente de revisar" />
      ) : (
        <ul className="divide-y divide-borde">
          {items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-3 py-3">
              <span className="min-w-0 flex-1">
                <span className="block font-medium">
                  {item.productCode} · {item.zoneId}
                </span>
                <span className="block text-xs text-tinta-suave">
                  confianza {Math.round(item.confidence * 100)}%
                </span>
              </span>
              <Button
                variant="secondary"
                onClick={async () => {
                  await api.reviewSampling(item.id, 'correct');
                  await load();
                }}
              >
                Correcta
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  // Un veredicto "incorrecta" NO se queda en estadistica: abre
                  // una excepcion para que alguien la arregle.
                  await api.reviewSampling(item.id, 'incorrect');
                  await load();
                }}
              >
                Incorrecta
              </Button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-tinta-suave">{t('admin.kpi.samplingError')}</p>
    </Card>
  );
}

function PricingPanel() {
  const t = useT();
  const [books, setBooks] = useState<Awaited<ReturnType<typeof api.priceBooks>>['items']>([]);
  const [threshold, setThreshold] = useState({ maxTotalCents: 60000, maxQty: 100 });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void api.priceBooks().then((response) => setBooks(response.items));
  }, []);

  return (
    <div className="space-y-3">
      <Card>
        <h2 className="mb-2 text-sm font-semibold">{t('admin.pricing')}</h2>
        <ul className="divide-y divide-borde text-sm">
          {books.map((book) => (
            <li key={book.id} className="flex items-center justify-between py-2">
              <span>
                v{book.version} · {book.ruleCount} reglas · IVA {book.vatBasisPoints / 100}%
              </span>
              {book.active ? <Badge tone="ok">activa</Badge> : <Badge>histórica</Badge>}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-tinta-suave">
          Una tarifa no se edita: se publica una versión nueva. Un presupuesto ya emitido tiene que poder explicarse con
          la tarifa con la que se calculó.
        </p>
      </Card>

      <Card className="space-y-3">
        <h2 className="text-sm font-semibold">Umbral pago / presupuesto</h2>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block text-xs uppercase text-tinta-suave">Importe máximo (€)</span>
            <input
              type="number"
              value={threshold.maxTotalCents / 100}
              onChange={(e) => setThreshold((c) => ({ ...c, maxTotalCents: Math.round(Number(e.target.value) * 100) }))}
              className="toque mt-1 w-full rounded-lg border border-borde px-3 py-2 tabular-nums"
            />
          </label>
          <label className="text-sm">
            <span className="block text-xs uppercase text-tinta-suave">Unidades máximas</span>
            <input
              type="number"
              value={threshold.maxQty}
              onChange={(e) => setThreshold((c) => ({ ...c, maxQty: Number(e.target.value) }))}
              className="toque mt-1 w-full rounded-lg border border-borde px-3 py-2 tabular-nums"
            />
          </label>
        </div>
        <Button
          onClick={async () => {
            await api.putThreshold({ ...threshold, techniquesRequiringQuote: ['embroidery'] });
            setSaved(true);
          }}
        >
          {t('app.save')}
        </Button>
        {saved && <Banner tone="ok" title="Umbral actualizado sin desplegar" />}
      </Card>
    </div>
  );
}

function WorkshopPanel() {
  const t = useT();
  const formatDate = useDate();
  const [items, setItems] = useState<Awaited<ReturnType<typeof api.workshopOrders>>['items']>([]);

  const load = () => api.workshopOrders().then((response) => setItems(response.items));

  useEffect(() => {
    void load();
  }, []);

  return (
    <Card>
      <h2 className="mb-3 text-sm font-semibold">{t('admin.workshop')}</h2>
      {items.length === 0 ? (
        <Empty title="Sin pedidos" />
      ) : (
        <ul className="divide-y divide-borde">
          {items.map((order) => (
            <li key={order.id} className="flex flex-wrap items-center gap-3 py-3">
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{order.reference}</span>
                <span className="block text-xs text-tinta-suave">
                  {order.techniqueId} · {order.totalQty} uds. · {formatDate(order.createdAt)}
                </span>
              </span>
              {order.packageReady ? <Badge tone="ok">paquete listo</Badge> : <Badge tone="warning">sin paquete</Badge>}
              <Badge>{t(`orders.statuses.${order.status}`)}</Badge>
              <select
                defaultValue=""
                onChange={async (e) => {
                  if (!e.target.value) return;
                  await api.updateOrderStatus(order.id, e.target.value);
                  await load();
                }}
                className="toque rounded-lg border border-borde px-2 py-1.5 text-sm"
              >
                <option value="">Cambiar estado…</option>
                {['PAID', 'IN_PRODUCTION', 'READY', 'DELIVERED', 'CANCELLED'].map((status) => (
                  <option key={status} value={status}>
                    {t(`orders.statuses.${status}`)}
                  </option>
                ))}
              </select>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
